import Plotly from "plotly.js-dist-min";
import store from "@/store";

const PLOT_BG_LIGHT = "#ffffff";
const PLOT_BG_DARK = "#374151";
const PAPER_BG_LIGHT = "#ffffff";
const PAPER_BG_DARK = "#111827";

function isDarkMode() {
  if (typeof document !== "undefined") {
    const vApp = document.querySelector("#app .v-application");
    if (vApp?.classList.contains("dark-mode")) return true;
    if (vApp?.classList.contains("light-mode")) return false;
    if (document.body?.classList.contains("dark-mode")) return true;
    if (document.body?.classList.contains("light-mode")) return false;
  }
  const stored = typeof localStorage !== "undefined" ? localStorage.getItem("darkMode") : null;
  if (stored === "true") return true;
  if (stored === "false") return false;
  try {
    return !!store.getters["darkMode/isDarkMode"];
  } catch {
    return false;
  }
}

function sanitizeFilenameBase(name) {
  return (
    String(name || "qaqc_plot")
      .trim()
      .replace(/\.png$/i, "")
      .replace(/[^a-zA-Z0-9._-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "qaqc_plot"
  );
}

function resolvePlotlyElement(gd) {
  if (!gd || typeof gd.querySelector !== "function") return null;
  return gd.classList?.contains("js-plotly-plot") ? gd : gd.querySelector(".js-plotly-plot");
}

function resolveExportBackgrounds(options = {}) {
  const dark = isDarkMode();
  const paper =
    options.paperBgcolor ??
    options.backgroundColor ??
    (dark ? PAPER_BG_DARK : PAPER_BG_LIGHT);
  const plot =
    options.plotBgcolor ??
    options.backgroundColor ??
    (dark ? PLOT_BG_DARK : PLOT_BG_LIGHT);
  return { paper_bgcolor: paper, plot_bgcolor: plot };
}

function clonePlotSnapshot(plotEl) {
  return {
    data: JSON.parse(JSON.stringify(plotEl.data || [])),
    layout: JSON.parse(JSON.stringify(plotEl.layout || {})),
  };
}

/**
 * @param {HTMLElement | null | undefined} gd - host element passed to Plotly.react / newPlot
 * @param {string} filenameBase - without extension
 * @param {{ paperBgcolor?: string, plotBgcolor?: string, backgroundColor?: string }} [options]
 * @returns {Promise<boolean>}
 */
export async function downloadPlotlyHostAsPng(gd, filenameBase, options = {}) {
  const plotEl = resolvePlotlyElement(gd);
  if (!plotEl?.data?.length) return false;

  const base = sanitizeFilenameBase(filenameBase);
  const w = Math.max(320, Math.round(plotEl.clientWidth || gd.clientWidth || gd.offsetWidth || 900));
  const h = Math.max(240, Math.round(plotEl.clientHeight || gd.clientHeight || gd.offsetHeight || 360));
  const { paper_bgcolor, plot_bgcolor } = resolveExportBackgrounds(options);
  const snapshot = clonePlotSnapshot(plotEl);
  snapshot.layout.paper_bgcolor = paper_bgcolor;
  snapshot.layout.plot_bgcolor = plot_bgcolor;
  snapshot.layout.width = w;
  snapshot.layout.height = h;
  snapshot.layout.autosize = false;

  const xAxisTitle = String(snapshot.layout?.xaxis?.title?.text ?? "").trim();
  if (xAxisTitle) {
    const margin = { ...(snapshot.layout.margin || {}) };
    margin.b = Math.max(Number(margin.b) || 0, 88);
    snapshot.layout.margin = margin;
  }

  const temp = document.createElement("div");
  temp.style.cssText = `position:fixed;left:-10000px;top:0;width:${w}px;height:${h}px;visibility:hidden;pointer-events:none;`;
  document.body.appendChild(temp);

  try {
    await Plotly.newPlot(temp, snapshot.data, snapshot.layout, {
      displayModeBar: false,
      responsive: false,
    });
    await Plotly.downloadImage(temp, {
      format: "png",
      filename: base,
      width: w,
      height: h,
    });
    return true;
  } catch {
    return false;
  } finally {
    try {
      Plotly.purge(temp);
    } catch {
      void 0;
    }
    temp.remove();
  }
}
